import { useState, useMemo } from 'react';
import { Plus, X, MapPin, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { LocationAutocomplete } from './LocationAutocomplete';

// Haversine distance in km between two {lat, lng} points
function distanceKm(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

const MAX_KM_BETWEEN = 3;

/**
 * Lets an organizer add an ordered list of checkpoints to an event.
 *
 * Each checkpoint: { name: string, lat: number, lng: number }
 *
 * Phase E rules enforced here (warnings, non-blocking):
 *   • Consecutive checkpoints must be ≤ 3 km apart.
 *   • Distance from `start` to checkpoint #1 must be ≤ 3 km.
 *   • Distance from final checkpoint to `finish` must be ≤ 3 km.
 *
 * UX:
 *   - Search bar with Google Places Autocomplete at the bottom.
 *     Picking a place adds a checkpoint to the list with its coords.
 *   - Each row shows numeric index (1, 2, 3…), name, coords, remove button.
 *   - Rows that violate the 3 km rule show a warning chip beneath.
 */
export function CheckpointEditor({ value = [], onChange, start, finish, country }) {
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  // Pre-compute distance violations once per render.
  const warnings = useMemo(() => {
    if (!value.length) return [];
    return value.map((cp, i) => {
      const prev = i === 0 ? start : value[i - 1];
      const d = distanceKm(prev, cp);
      if (d != null && d > MAX_KM_BETWEEN) return d;
      return null;
    });
  }, [value, start]);

  // Check distance from last checkpoint to finish line.
  const tailWarning = useMemo(() => {
    if (!value.length || !finish) return null;
    const d = distanceKm(value[value.length - 1], finish);
    return d != null && d > MAX_KM_BETWEEN ? d : null;
  }, [value, finish]);

  function addCheckpoint(cp) {
    if (!cp?.lat || !cp?.lng) return;
    onChange([...value, cp]);
    setSearch('');
  }

  function removeAt(idx) {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  }

  function handlePlace(place) {
    const loc = place?.geometry?.location;
    if (!loc) return;
    setBusy(true);
    addCheckpoint({
      name: place.name || place.formatted_address || `Checkpoint ${value.length + 1}`,
      lat: loc.lat(),
      lng: loc.lng(),
    });
    setBusy(false);
  }

  return (
    <div className="space-y-3" data-testid="checkpoint-editor">
      {value.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-border p-4 text-center" data-testid="checkpoint-empty">
          <MapPin className="h-5 w-5 text-muted-foreground mx-auto" />
          <p className="text-xs text-muted-foreground mt-1">No checkpoints yet. Add the first below.</p>
          {!start && (
            <p className="text-[10px] text-destructive mt-1">
              Tip: drop a start point on the map first so we can check the 3 km rule.
            </p>
          )}
        </div>
      ) : (
        <div className="rivl-card divide-y divide-border" data-testid="checkpoint-list">
          {value.map((cp, i) => (
            <div key={`${cp.lat}-${cp.lng}-${i}`} className="flex items-center gap-3 px-3 py-2" data-testid={`checkpoint-row-${i}`}>
              <div className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center font-mono font-bold text-xs flex-shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{cp.name || `Checkpoint ${i + 1}`}</div>
                <div className="font-mono text-[10px] text-muted-foreground truncate">{Number(cp.lat).toFixed(4)}, {Number(cp.lng).toFixed(4)}</div>
                {warnings[i] != null && (
                  <div
                    className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-destructive"
                    data-testid={`checkpoint-warning-${i}`}
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {warnings[i].toFixed(1)} km from previous (max 3 km)
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="h-7 w-7 rounded-md hover:bg-secondary flex items-center justify-center"
                data-testid={`checkpoint-remove-${i}`}
                aria-label="Remove checkpoint"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {tailWarning != null && (
            <div
              className="px-3 py-2 flex items-center gap-1.5 text-[11px] text-destructive font-semibold"
              data-testid="checkpoint-warning-finish"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Last checkpoint is {tailWarning.toFixed(1)} km from the finish (max 3 km).
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <LocationAutocomplete
          value={search}
          onChange={setSearch}
          onPlace={handlePlace}
          country={country}
          placeholder="Search address or landmark to add..."
          className="h-11 rounded-md text-sm"
          data-testid="checkpoint-search"
        />
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => {
            if (!search) return;
            addCheckpoint({ name: search, lat: 0, lng: 0 });
          }}
          className="w-full h-10 rounded-md text-xs"
          data-testid="checkpoint-add-fallback"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add checkpoint</>}
        </Button>
      </div>
    </div>
  );
}
